import { yupResolver } from '@hookform/resolvers/yup';
import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { Button, Dialog } from '@theguarantors/tg-package-material-ui';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { setUserQueryData, useUserQuery } from '@/shared/session';
import userValues from '../../utils/normalizeUser';
import postUser from '../../utils/apiRestCalls/postUser';
import { capitalizeFirstLetter, normalizePhoneNumber } from '../../utils/stringUtils';
import { PhoneNumberFormat } from '@/shared/components/PhoneNumberFormat';
import { type AxiosUserResponse } from '../../utils/types/User';
import useProfileDrawerStore from '../../stores/useProfileDrawerStore';
import { profileSchema } from '../../utils/validator/userValidator';
import logout from '@/utils/axios/logout';
import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';
import { useState } from 'react';
import '@theguarantors/tg-package-material-ui/dist/fonts/index.css';

interface IFormInputs {
  firstName: string;
  lastName: string;
  phone?: string;
  email: string;
}

function RegistrationProfile() {
  const { hideProfileDrawer } = useProfileDrawerStore();
  const { track } = useAnalyticsContext();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleLogoutConfirm = () => {
    track('Logout Button is clicked', {
      title: 'Logout',
      referrer: window.location.pathname,
    });
    logout({ reason: 'user_initiated' });
  };

  const { data: user } = useUserQuery();

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (userInfo: IFormInputs) => {
      return postUser(userInfo);
    },
    onSuccess: async (response: AxiosUserResponse) => {
      setUserQueryData(queryClient, (cachedUser) => {
        if (!cachedUser) {
          return cachedUser;
        }

        return {
          ...cachedUser,
          firstName: userValues(response)?.firstName,
          lastName: userValues(response)?.lastName,
          phone: userValues(response)?.phone,
          email: userValues(response)?.email,
        };
      });
      hideProfileDrawer();
    },
  });

  const submit = async (data: IFormInputs) => {
    mutation.mutate(data);
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<IFormInputs>({
    mode: 'onChange',
    resolver: yupResolver(profileSchema),
  });

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Controller
        name="email"
        control={control}
        defaultValue={user?.email ?? ''}
        render={({ field }) => (
          <TextField
            {...field}
            id="emailAddressId"
            label="Email address"
            error={Boolean(errors.email)}
            disabled={true}
            type="email"
            helperText={errors?.email?.message ?? ' '}
            InputProps={{
              classes: {
                root: 'fs-mask',
              },
            }}
          />
        )}
      />
      <Controller
        name="firstName"
        control={control}
        defaultValue={user?.firstName ?? ''}
        render={({ field }) => (
          <TextField
            {...field}
            required={true}
            ref={null}
            id="firstName"
            label="First name"
            error={Boolean(errors.firstName)}
            helperText={errors.firstName?.message ?? ' '}
            onChange={(e: { target: { value: string } }) =>
              field.onChange(capitalizeFirstLetter(e.target.value))
            }
            InputProps={{
              classes: {
                root: 'fs-mask',
              },
            }}
          />
        )}
      />
      <Controller
        name="lastName"
        control={control}
        defaultValue={user?.lastName ?? ''}
        render={({ field }) => (
          <TextField
            {...field}
            required={true}
            ref={null}
            id="lastName"
            label="Last name"
            error={Boolean(errors.lastName)}
            helperText={errors.lastName?.message ?? ' '}
            onChange={(e: { target: { value: string } }) =>
              field.onChange(capitalizeFirstLetter(e.target.value))
            }
            InputProps={{
              classes: {
                root: 'fs-mask',
              },
            }}
          />
        )}
      />
      <Controller
        name="phone"
        control={control}
        defaultValue={normalizePhoneNumber(user?.phone)}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value || ''}
            label="Phone number"
            type="tel"
            ref={null}
            error={Boolean(errors.phone)}
            id="PhoneNumber"
            helperText={errors?.phone?.message ?? 'Message and data rates may apply'}
            slotProps={{
              input: {
                inputComponent: PhoneNumberFormat,
                startAdornment: (
                  <InputAdornment position="start">
                    <span>+ 1</span>
                  </InputAdornment>
                ),
                inputProps: {
                  name: 'phone',
                },
                classes: {
                  root: 'fs-mask',
                },
              },
            }}
          />
        )}
      />
      <Stack
        direction="row"
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          mt: '32px',
        }}
      >
        <Button type="button" variant="outlined" color="error" onClick={handleLogoutClick}>
          Logout
        </Button>
        <Tooltip title={!isValid ? 'Please complete the required fields to proceed' : ''}>
          <span style={{ display: 'inline-block' }}>
            <Button
              id="EnrollmentButton"
              variant="contained"
              loading={mutation.isPending}
              type="submit"
              disabled={!isValid || isSubmitting}
            >
              Save & Close
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        slotProps={{
          paper: {
            sx: {
              width: '408px',
              minHeight: '148px',
              padding: '32px',
            },
          },
        }}
      >
        <DialogTitle sx={{ padding: '0px !important', lineHeight: '24px !important' }}>
          Log out of theguarantors.com
        </DialogTitle>
        <DialogContent sx={{ padding: 0, mt: 1, lineHeight: '24px !important' }}>
          <DialogContentText>Are you sure you wish to log out?</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 0, mt: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained">
            Logout
          </Button>
          <Button onClick={handleLogoutCancel}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </form>
  );
}
export default RegistrationProfile;
